/**
 * cloudUrls — engine_backend media-plane URLs + per-device capability classifier
 * for the cloud viewer.
 *
 * LIVE is uniform across ALL cloud devices (VERIFIED vs prod 2026-07-16): every
 * streaming cloud device — dragon AND legacy — pushes raw H.264 Annex-B over a
 * token-authed WebSocket off engine_backend, keyed by device ID (MAC-hex). There
 * is no fragmented-MP4/MSE or HLS live subset. So `buildCloudLiveUrl` is the ONE
 * live builder; the CloudNvrAdapter appends the `?token=` (session state) to it.
 * (The Java `dview` CloudModel uses this same H.264 path; the isite MSE portal
 * was the unused one.)
 *
 * Playback/thumbnail/config still carry a dragon-vs-regular split that has not
 * been verified against prod yet (Day-3): `classifyDevice` is the single
 * discriminator for those, default-safe (an unrecognized device classifies as
 * REGULAR). It no longer gates the live player.
 *
 * The media origin is deployment config. Hosted builds point it at CloudAPI,
 * which authenticates and proxies recorded Dragon media. Direct engine targets
 * remain useful for local development.
 */

/** Testbed engine_backend (Brad's box). Ryan overrides for staging/prod. */
const DEFAULT_ENGINE_BACKEND = 'http://web1.dividia.net:888';
const TRUSTED_CLOUD_TOKEN_ORIGINS = new Set([
  'https://api.cloud.dividia.net',
  'https://api-staging.cloud.dividia.net',
]);

/** Return true only when a URL can receive a cloud session JWT. */
export const canAttachCloudToken = (rawUrl) => {
  try {
    const url = new URL(rawUrl);
    const httpOrigin = url.protocol === 'wss:' ? `https://${url.host}` : url.origin;
    return TRUSTED_CLOUD_TOKEN_ORIGINS.has(httpOrigin);
  } catch (_) {
    return false;
  }
};

/**
 * Resolve the engine_backend origin into http + ws forms. Accepts a bare
 * `host:port` or a full `scheme://host:port`; a bare host defaults to http.
 * The ws scheme tracks the http scheme (https→wss, http→ws).
 * @returns {{httpOrigin:string, wsOrigin:string}}
 */
export const getEngineBackend = () => {
  const raw = process.env.REACT_APP_ENGINE_BACKEND || DEFAULT_ENGINE_BACKEND;
  // Accept http(s)://, ws(s)://, or a bare host[:port]. URL() can't parse a
  // ws(s):// origin, so normalize ws→http / wss→https for parsing; the secure
  // bit (from wss/https) is what actually drives the ws/wss + http/https output,
  // so the ws(s):// form round-trips correctly (a bare host defaults to http/ws).
  let normalized = raw;
  if (/^wss:\/\//i.test(raw)) normalized = raw.replace(/^wss:\/\//i, 'https://');
  else if (/^ws:\/\//i.test(raw)) normalized = raw.replace(/^ws:\/\//i, 'http://');
  else if (!/^https?:\/\//i.test(raw)) normalized = `http://${raw}`;
  let host, secure;
  try {
    const u = new URL(normalized);
    host = u.host;
    secure = u.protocol === 'https:';
  } catch (_) {
    // Malformed override — fall back to the testbed rather than emit a bad URL.
    const u = new URL(DEFAULT_ENGINE_BACKEND);
    host = u.host;
    secure = false;
  }
  return {
    httpOrigin: `${secure ? 'https' : 'http'}://${host}`,
    wsOrigin: `${secure ? 'wss' : 'ws'}://${host}`,
  };
};

/**
 * Base for recorded Dragon media. Hosted builds use CloudAPI for authenticated
 * MP4, JPEG, and JSON proxying. Local development can still use a direct engine
 * origin. Live WebSockets use the same configured host and their own token.
 */
export const getMediaBase = () => getEngineBackend().httpOrigin;

// ---- Dragon media URLs ----------------------------------------------------
// Resource naming (VERIFIED §5): `<devID>-cam{N}-...` where devID is 12-char
// MAC-hex, N is the 1-based camera number, P the profile, ts a 14-digit
// yyyymmddhhmmss stamp.

/** Live WS URL — the browser just opens it; engine_backend auto-inits the push. */
export const buildCloudLiveUrl = (deviceId, cam, profile = 1) =>
  `${getEngineBackend().wsOrigin}/${deviceId}-cam${cam}-pro${profile}`;

/**
 * Playback MP4 URL — byte-range seekable; feed straight to PlaybackPlayer (fetch-streamed).
 * `pro2` selects the LIVE-profile twin recording (Dragon's record_pro2): engine_backend's
 * ws_media_server accepts a `.pro2.mp4` tail (parse_playback_pathname) and serves the VIDEO2
 * twin, 404ing fast when a clip has no twin. Default false = the main clip (`.mp4`).
 */
export const buildCloudClipUrl = (deviceId, cam, ts, pro2 = false) =>
  `${getMediaBase()}/${deviceId}-cam${cam}-${ts}${pro2 ? '.pro2' : ''}.mp4`;

/** JPEG thumbnail/snapshot for a recorded moment. */
export const buildCloudThumbUrl = (deviceId, cam, ts) =>
  `${getMediaBase()}/${deviceId}-cam${cam}-${ts}.jpg`;

/** AI metadata sidecar ({scene, objects[]}) for a recorded moment. */
export const buildCloudMetadataUrl = (deviceId, cam, ts) =>
  `${getMediaBase()}/${deviceId}-cam${cam}-${ts}.json`;

// ---- Legacy field-fleet recorded-clip URL (REGULAR devices) ---------------
// Ported EXACTLY from Java cloud playback: DView.java sets sCloudUrl =
// "https://api.cloud.dividia.net" (a FIXED host, NOT the project's own url field),
// and PlaybackStream.java:182 / MCPlayerController.java:482 build the clip URL as
//   sCloudUrl + "/uploads" + file
// where file = "/<project.sDir><devID>/cam<N>/video/<utcTs>.<ext>" and sDir is the
// project's storage dir (e.g. "base/ayerslo/"). VERIFIED: the api.cloud host serves
// these (HTTP 206 video/mp4); the project's own url host (ayerslo.cloud…) 502s.

/**
 * Cloud-uploads base for recorded clips (Java sCloudUrl + "/uploads"). ENV-DRIVEN so it
 * moves cleanly across local/staging/prod (`REACT_APP_CLOUD_UPLOADS`):
 *   - staging/prod → set it to the clip host, e.g. "https://api.cloud.dividia.net".
 *     nvr.cloud.dividia.net and api.cloud.dividia.net are SAME-SITE, so the cloud host's
 *     `Cross-Origin-Resource-Policy: same-site` header lets the <video> load it.
 *   - dev (unset) → RELATIVE `/uploads`, served same-origin via setupProxy. A cross-origin
 *     <video> straight to the cloud host is blocked (net::ERR_BLOCKED_BY_RESPONSE.NotSameSite)
 *     from a non-dividia.net dev origin (127.0.0.1); the same-origin proxy avoids that.
 */
export const getCloudUploadsBase = () => {
  const override = process.env.REACT_APP_CLOUD_UPLOADS;
  // Always ABSOLUTE so the built clip URL passes straight through buildStreamUrl
  // (its http-passthrough branch) with no session token appended. Dev (no override) =
  // the page's own origin, so the URL is same-origin and served via the setupProxy
  // /uploads route (no CORP block); prod = the configured cloud host (same-site).
  const base = override
    ? String(override).replace(/\/+$/, '')
    : (typeof window !== 'undefined' && window.location ? window.location.origin : '');
  return `${base}/uploads`;
};

/**
 * Legacy recorded-clip URL: <uploadsBase>/<project.sDir><devID>/cam<N>/video/<utcTs>.<ext>.
 * @param {Object} project - the JSCustomerProject record (needs `sDir`, e.g. "base/ayerslo/")
 */
export const buildCloudLegacyClipUrl = (project, deviceId, cam, utcTs, ext = 'mp4') => {
  const sDir = project && project.sDir;
  if (!sDir || !deviceId || !utcTs) return '';
  const dir = String(sDir).replace(/^\/+/, '').replace(/\/*$/, '/'); // no leading slash, one trailing
  return `${getCloudUploadsBase()}/${dir}${deviceId}/cam${cam}/video/${utcTs}.${ext}`;
};

/** Legacy recorded-clip thumbnail (same path, .jpg). */
export const buildCloudLegacyThumbUrl = (project, deviceId, cam, utcTs) =>
  buildCloudLegacyClipUrl(project, deviceId, cam, utcTs, 'jpg');

// ---- Capability classification --------------------------------------------

/**
 * Does this device serve media DIRECTLY off engine_backend (dragon), vs. via the
 * cloudapi `/ve` proxy (regular field fleet)?
 *
 * ⚠ DISCRIMINATOR — CONFIRM LIVE (W2 step 5): checked in priority order so the
 * signal can be tightened against the real device record without touching call
 * sites. Default is FALSE (regular) so an unrecognized device stays on the
 * production-safe proxied path.
 *   1. explicit boolean marker (future backend field / test override)
 *   2. a device-type string that names dragon
 *   3. the Phase 6 JSON envelope, or dragon-only sections in an INI config
 *      (files.dragon / llm / videostore — regular devices have none)
 * @param {Object} device - a `/ve` device record
 * @returns {boolean}
 */
const detectDirectMedia = (device = {}) => {
  // 1. explicit markers
  if (typeof device.directMedia === 'boolean') return device.directMedia; // test/backend override — authoritative
  // fDragon is POSITIVE-ONLY: CloudAPI publishes fDragon:false even for modern devices
  // (alongside isModern:true / sConfigFormat:"json"), so a false fDragon must NOT shadow the
  // isModern / device-type / config checks below. Only trust fDragon when it is explicitly true.
  if (device.fDragon === true) return true;
  if (device.isModern === true) return true;

  // 2. device-type string
  const type = (device.sDeviceType || device.sType || '').toLowerCase();
  if (type.includes('dragon')) return true;

  // 3. dragon-only config sections
  const cfg = device.sConfig;
  if (cfg && typeof cfg === 'object') {
    if (cfg.format === 1 && cfg.files && cfg.files.dragon) return true;
    if (cfg.llm || cfg.videostore) return true;
  } else if (typeof cfg === 'string' && cfg) {
    if (/\[\s*llm\s*\]/i.test(cfg) || /\bvideostore\b/i.test(cfg)) return true;
  }

  return false;
};

/**
 * Capability descriptor for a device. The rest of the viewer branches on THESE
 * flags, never on device identity — the one place the dragon-vs-regular
 * distinction is made is detectDirectMedia() above.
 * @param {Object} [device] - a `/ve` device record
 * @returns {{directMedia:boolean, proxiedMedia:boolean, richConfig:boolean}}
 */
export const classifyDevice = (device = {}) => {
  const directMedia = detectDirectMedia(device);
  return {
    directMedia,               // WS live + MP4 playback straight off engine_backend
    proxiedMedia: !directMedia, // pbctrl/HLS/snapshot/mjpeg via cloudapi /ve
    richConfig: directMedia,   // dragon INI blob (videostore/llm/lpr_roi/motion-grid/…)
  };
};
